'use client'

import { type PropsWithChildren, useState, type FC, isValidElement } from 'react'
import { XIcon, PlusIcon, FileText, Loader2Icon, AlertCircleIcon } from 'lucide-react'
import { AttachmentPrimitive, ComposerPrimitive, MessagePrimitive, useAuiState, useAui } from '@assistant-ui/react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import { Dialog, DialogTitle, DialogContent, DialogTrigger } from '@/components/shadcn/dialog'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/shadcn/avatar'
import { useAttachmentSrc } from '@/hooks/use-attachment-src'
import { cn } from '@/lib/utils'
import { TooltipIconButton } from './tooltip-icon-button'

type AttachmentPreviewProps = {
  src: string
}

const AttachmentPreview: FC<AttachmentPreviewProps> = ({ src }) => {
  const [isLoaded, setIsLoaded] = useState(false)
  return (
    <img
      src={src}
      alt="Attachment preview"
      className={cn(
        'block h-auto max-h-[80vh] w-auto max-w-full rounded-sm object-contain transition-opacity duration-300 motion-reduce:transition-none',
        isLoaded ? 'aui-attachment-preview-image-loaded opacity-100' : 'aui-attachment-preview-image-loading opacity-0'
      )}
      onLoad={() => setIsLoaded(true)}
    />
  )
}

const AttachmentPreviewDialog: FC<PropsWithChildren> = ({ children }) => {
  const src = useAttachmentSrc()

  if (!src) return children

  return (
    <Dialog>
      <DialogTrigger className="aui-attachment-preview-trigger cursor-zoom-in" asChild>
        {isValidElement(children) ? children : <button type="button">{children}</button>}
      </DialogTrigger>
      <DialogContent className="aui-attachment-preview-dialog-content [&>button]:ring-0! p-2 sm:max-w-3xl [&>button]:rounded-full [&>button]:bg-foreground/60 [&>button]:p-1 [&>button]:opacity-100 [&>button]:hover:bg-foreground/80 [&_svg]:text-background">
        <DialogTitle className="aui-sr-only sr-only">Image Attachment Preview</DialogTitle>
        <div className="aui-attachment-preview relative mx-auto flex max-h-[80dvh] w-full items-center justify-center overflow-hidden rounded-sm bg-background">
          <AttachmentPreview src={src} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

const AttachmentThumb: FC = () => {
  const src = useAttachmentSrc()

  return (
    <Avatar className="aui-attachment-tile-avatar h-full w-full rounded-none">
      <AvatarImage src={src} alt="Attachment preview" className="aui-attachment-tile-image rounded-none object-cover" />
      <AvatarFallback>
        <FileText className="aui-attachment-tile-fallback-icon size-6 stroke-[1.5] text-muted-foreground/80" />
      </AvatarFallback>
    </Avatar>
  )
}

const AttachmentUI: FC = () => {
  const aui = useAui()
  const isComposer = aui.attachment.source !== 'message'

  const isImage = useAuiState((s) => s.attachment.type === 'image')
  const attachmentName = useAuiState((s) => s.attachment.name)
  const typeLabel = useAuiState((s) => {
    const type = s.attachment.type
    switch (type) {
      case 'image':
        return 'Image'
      case 'document':
        return 'Document'
      case 'file':
        return 'File'
      default:
        return type
    }
  })

  // An attachment on a submission is still being prepared, whether or not the
  // adapter reports progress while it uploads.
  const uploadState = useAuiState((s) =>
    s.attachment.status.type === 'incomplete' && s.attachment.status.reason === 'error'
      ? 'error'
      : s.attachment.status.type === 'running' || s.attachment.status.type === 'requires-action'
        ? 'uploading'
        : undefined
  )
  const isUploading = uploadState === 'uploading'
  const isError = uploadState === 'error'

  const errorMessage = useAuiState((s) =>
    s.attachment.status.type === 'incomplete' && s.attachment.status.reason === 'error'
      ? (s.attachment.status.message ?? 'Upload failed')
      : undefined
  )

  return (
    <TooltipProvider>
      <Tooltip>
        <AttachmentPrimitive.Root
          className={cn(
            'aui-attachment-root relative',
            isComposer && 'duration-200 animate-in fade-in-0 zoom-in-95 motion-reduce:animate-none',
            isImage && !isComposer && 'aui-attachment-root-message only:*:first:size-24'
          )}
        >
          <AttachmentPreviewDialog>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'aui-attachment-tile relative size-14 cursor-pointer overflow-hidden rounded-[calc(var(--composer-radius,1rem)-var(--composer-padding,8px))] bg-muted outline-none transition-transform after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:ring-1 after:ring-inset after:ring-black/10 after:transition-colors hover:after:bg-foreground/10 focus-visible:ring-1 focus-visible:ring-ring/50 active:scale-[0.96] motion-reduce:transition-none dark:after:ring-white/10',
                  isError && 'after:ring-destructive/60 dark:after:ring-destructive/60'
                )}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.currentTarget.click()
                  } else if (e.key === ' ') {
                    e.preventDefault()
                  }
                }}
                onKeyUp={(e) => {
                  if (e.key === ' ') e.currentTarget.click()
                }}
                aria-label={`${typeLabel} attachment ${attachmentName}${
                  isError ? ', upload failed' : isUploading ? ', uploading' : ''
                }`}
              >
                <AttachmentThumb />
                {isUploading && (
                  <div
                    aria-hidden="true"
                    className="aui-attachment-tile-uploading absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[2px] animate-in fade-in-0 motion-reduce:animate-none"
                  >
                    <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                {isError && (
                  <div
                    aria-hidden="true"
                    className="aui-attachment-tile-error absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-[2px] animate-in fade-in-0 motion-reduce:animate-none"
                  >
                    <AlertCircleIcon className="size-4 text-destructive" />
                  </div>
                )}
              </div>
            </TooltipTrigger>
          </AttachmentPreviewDialog>
          {isComposer && <AttachmentRemove />}
        </AttachmentPrimitive.Root>
        <TooltipContent side="top">
          <AttachmentPrimitive.Name />
          {errorMessage && <p className="aui-attachment-error-message">{errorMessage}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const AttachmentRemove: FC = () => {
  return (
    <AttachmentPrimitive.Remove asChild>
      <TooltipIconButton
        tooltip="Remove file"
        className="aui-attachment-tile-remove bg-black/50! hover:bg-black/70! hover:text-white! absolute end-1 top-1 size-5 rounded-full text-white after:absolute after:-inset-1.5 active:scale-[0.96] motion-reduce:transition-none"
        side="top"
      >
        <XIcon className="aui-attachment-remove-icon size-3 stroke-[2.5]" />
      </TooltipIconButton>
    </AttachmentPrimitive.Remove>
  )
}

export const UserMessageAttachments: FC = () => {
  return (
    <div className="aui-user-message-attachments-end col-span-full col-start-1 row-start-1 flex w-full flex-row justify-end gap-2 empty:hidden">
      <MessagePrimitive.Attachments>{() => <AttachmentUI />}</MessagePrimitive.Attachments>
    </div>
  )
}

export const ComposerAttachments: FC = () => {
  return (
    <div className="aui-composer-attachments flex w-full flex-row items-center gap-2 overflow-x-auto empty:hidden">
      <ComposerPrimitive.Attachments>{() => <AttachmentUI />}</ComposerPrimitive.Attachments>
    </div>
  )
}

export const ComposerAddAttachment: FC = () => {
  return (
    <ComposerPrimitive.AddAttachment asChild>
      <TooltipIconButton
        tooltip="Add Attachment"
        side="bottom"
        variant="ghost"
        size="icon"
        className="aui-composer-add-attachment size-7 rounded-full text-muted-foreground hover:bg-muted-foreground/15 hover:text-foreground active:scale-[0.96] motion-reduce:transition-none dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30"
        aria-label="Add Attachment"
      >
        <PlusIcon className="aui-attachment-add-icon size-4" />
      </TooltipIconButton>
    </ComposerPrimitive.AddAttachment>
  )
}
